import { AvailabilityModal } from '@/components/dashboard/calendar/AvailabilityModal';
import { ShareCalendarModal } from '@/components/dashboard/calendar/ShareCalendarModal';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
  Card,
  CardAction,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Booking, useBookingQueries } from '@/hooks/queries/useBookingQueries';
import { usePropertyQueries } from '@/hooks/queries/usePropertyQueries';
import { useAuthWithProfile } from '@/hooks/useAuth';
import { toast } from '@/hooks/useToast';
import { Property } from '@/types/property';
import { createFileRoute } from '@tanstack/react-router';
import {
  addDays,
  addMinutes,
  format,
  isSameDay,
  isToday,
  startOfWeek,
  subDays,
} from 'date-fns';
import { CalendarIcon, ChevronLeft, ChevronRight, Plus, Share2, Trash2 } from 'lucide-react';
import { Suspense, useState } from 'react';

export const Route = createFileRoute('/_protected/dashboard/calendar')({
  component: CalendarPage,
});

// Booking interface
interface Booking2 {
  booking_id: string;
  property_id: string;
  visitor_name: string;
  visitor_email: string;
  visitor_phone: string;
  start_datetime: Date;
  end_datetime: Date;
  notes: string | null;
  status: 'active' | 'cancelled';
  created_at: Date;
  updated_at: Date;
}

// Mock bookings data with property_id for all entries
const mockBookings: Booking2[] = [
  {
    booking_id: '1',
    property_id: 'ce0eb4d8-d02b-418c-9e27-f2a65220e03d', // Ensure this ID can be resolved by useFetchProperty or adjust if needed
    visitor_name: 'Sarah Johnson',
    visitor_email: 'sarah.j@email.com',
    visitor_phone: '+1 (555) 123-4567',
    start_datetime: new Date(),
    end_datetime: addMinutes(new Date(), 30),
    notes: 'First-time buyer interested in downtown properties',
    created_at: new Date(),
    updated_at: new Date(),
    status: 'active',
  },
  {
    booking_id: '2',
    property_id: 'ce0eb4d8-d02b-418c-9e27-f2a65220e03d',
    visitor_name: 'Michael Chen',
    visitor_email: 'm.chen@email.com',
    visitor_phone: '+1 (555) 987-6543',
    start_datetime: addDays(new Date(), 1),
    end_datetime: addMinutes(addDays(new Date(), 1), 30),
    notes: 'Looking for investment opportunities',
    created_at: new Date(),
    updated_at: new Date(),
    status: 'active',
  },
  {
    booking_id: '3',
    property_id: 'ce0eb4d8-d02b-418c-9e27-f2a65220e03d',
    visitor_name: 'Emma Wilson',
    visitor_email: 'emma.wilson@email.com',
    visitor_phone: '+1 (555) 456-7890',
    start_datetime: addDays(new Date(), 2),
    end_datetime: addMinutes(addDays(new Date(), 2), 60), // Assuming a duration
    notes: 'Needs detailed market analysis for the area',
    created_at: new Date(),
    updated_at: new Date(),
    status: 'cancelled',
  },
  {
    booking_id: '4',
    property_id: 'ce0eb4d8-d02b-418c-9e27-f2a65220e03d',
    visitor_name: 'David Rodriguez',
    visitor_email: 'david.r@email.com',
    visitor_phone: '+1 (555) 234-5678',
    start_datetime: addDays(new Date(), 3),
    end_datetime: addMinutes(addDays(new Date(), 3), 45),
    notes: 'Relocating from Vancouver, needs quick viewing',
    created_at: new Date(),
    updated_at: new Date(),
    status: 'active',
  },
  {
    booking_id: '5',
    property_id: 'ce0eb4d8-d02b-418c-9e27-f2a65220e03d',
    visitor_name: 'Maria Garcia',
    visitor_email: 'maria.g@email.com',
    visitor_phone: '+1 (555) 567-8901',
    start_datetime: addDays(new Date(), 8),
    end_datetime: addMinutes(addDays(new Date(), 8), 30),
    notes: 'Portfolio discussion',
    created_at: new Date(),
    updated_at: new Date(),
    status: 'active',
  },
  {
    booking_id: '6',
    property_id: 'ce0eb4d8-d02b-418c-9e27-f2a65220e03d',
    visitor_name: 'Robert Kim',
    visitor_email: 'robert.kim@email.com',
    visitor_phone: '+1 (555) 678-9012',
    start_datetime: addDays(new Date(), 9),
    end_datetime: addMinutes(addDays(new Date(), 9), 30),
    notes: '',
    created_at: new Date(),
    updated_at: new Date(),
    status: 'active',
  },
];

// --- Helper to format property address ---
function formatPropertyAddress(property: Property | undefined | null): string {
  if (!property) return 'Address not available';
  const parts = [
    property.street_number,
    property.street_name,
    property.street_abbrev,
  ].filter(Boolean).join(' ');
  const cityStateZip = [
    property.municipality,
    property.state_province,
    property.postal_code,
  ].filter(Boolean).join(', ');

  if (parts && cityStateZip) return `${parts}, ${cityStateZip}`;
  if (parts) return parts;
  if (cityStateZip) return cityStateZip;
  return `Property ID: ${property.property_id}`;
}

// --- BookingCard Component ---
interface BookingCardProps {
  booking: Booking2;
  onCancelBooking: (bookingId: string) => void;
}

function BookingCard({ booking, onCancelBooking }: BookingCardProps) {
  const { useFetchProperty } = usePropertyQueries();
  const { data: property } = useFetchProperty(booking.property_id);

  return (
    <Card className={`transition-shadow hover:shadow-md ${booking.status === 'cancelled' ? 'border-red-200 bg-red-50/50 dark:bg-red-900/30' : 'border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800'}`}>
      <CardHeader className="pb-2.5">
        <div className="flex justify-between items-start gap-2">
          <div>
            <CardTitle className="text-base font-semibold text-slate-800 dark:text-slate-100">
              {formatPropertyAddress(property)}
            </CardTitle>
            <CardDescription className="text-xs text-slate-500 dark:text-slate-400">
              {property?.freehold_property_type ? `Type: ${property.freehold_property_type}` : 'Property details loading...'}
            </CardDescription>
          </div>
          {booking.status === 'active' && (
            <Button
              variant="ghost"
              className="text-red-500 hover:text-red-700 hover:bg-red-100 dark:hover:bg-red-800/50 h-7 w-7 shrink-0"
              onClick={() => onCancelBooking(booking.booking_id)}
              aria-label="Cancel booking"
            >
              <Trash2 className="h-4 w-4" />
            </Button>
          )}
        </div>
      </CardHeader>
      <CardContent className="space-y-2 text-sm">
        <div>
          <p className="font-medium text-slate-700 dark:text-slate-200">{booking.visitor_name}</p>
          <p className="text-xs text-slate-500 dark:text-slate-400">
            {format(booking.start_datetime, 'p')} - {format(booking.end_datetime, 'p')}
          </p>
        </div>

        {booking.notes && (
          <div className="text-xs text-slate-600 dark:text-slate-300 border-t border-slate-100 dark:border-slate-700 pt-1.5 mt-1.5">
            <p className="font-medium text-slate-700 dark:text-slate-200">Notes:</p>
            <p className="whitespace-pre-wrap">{booking.notes}</p>
          </div>
        )}

        <div className="border-t border-slate-100 dark:border-slate-700 pt-1.5 mt-1.5">
          <p className="text-xs font-medium text-slate-700 dark:text-slate-200 mb-0.5">Contact:</p>
          <p className="text-xs text-slate-600 dark:text-slate-300">Email: {booking.visitor_email}</p>
          <p className="text-xs text-slate-600 dark:text-slate-300">Phone: {booking.visitor_phone}</p>
        </div>

        {booking.status === 'cancelled' && (
          <Badge variant="destructive" className="mt-2 text-xs">Booking Cancelled</Badge>
        )}
      </CardContent>
    </Card>
  );
}

// --- DayDetailDialog Component ---
interface DayDetailDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  selectedDayDate: Date | null;
  bookingsForDay: Booking2[];
  onCancelBooking: (bookingId: string) => void;
}

function DayDetailDialog({ open, onOpenChange, selectedDayDate, bookingsForDay, onCancelBooking }: DayDetailDialogProps) {
  if (!selectedDayDate) return null;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md sm:max-w-lg md:max-w-xl bg-white dark:bg-slate-800">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-lg font-semibold text-slate-800 dark:text-slate-100">
            <CalendarIcon className="h-5 w-5 text-blue-600 dark:text-blue-400" />
            {format(selectedDayDate, 'EEEE, MMMM d, yyyy')}
          </DialogTitle>
        </DialogHeader>

        <div className="py-3 space-y-3">
          {bookingsForDay.length > 0 ? (
            <div className="space-y-3 max-h-[60vh] overflow-y-auto pr-2 -mr-2 custom-scrollbar">
              <h3 className="text-sm font-semibold text-slate-700 dark:text-slate-200 sticky top-0 bg-white dark:bg-slate-800 py-1 z-10">
                Scheduled Events ({bookingsForDay.length})
              </h3>
              {bookingsForDay.map((booking) => (
                <Suspense
                  key={booking.booking_id}
                  fallback={
                    <Card className="border-slate-200 dark:border-slate-700 animate-pulse bg-slate-50 dark:bg-slate-700/50">
                      <CardHeader className="pb-2"><div className="h-4 bg-slate-200 dark:bg-slate-600 rounded w-3/4"></div></CardHeader>
                      <CardContent className="space-y-1.5">
                        <div className="h-3 bg-slate-200 dark:bg-slate-600 rounded w-1/2"></div>
                        <div className="h-3 bg-slate-200 dark:bg-slate-600 rounded w-full"></div>
                      </CardContent>
                    </Card>
                  }
                >
                  <BookingCard booking={booking} onCancelBooking={onCancelBooking} />
                </Suspense>
              ))}
            </div>
          ) : (
            <div className="text-center py-8">
              <CalendarIcon className="h-12 w-12 text-slate-300 dark:text-slate-600 mx-auto mb-3" />
              <p className="text-slate-500 dark:text-slate-400 text-md">No events scheduled for this day.</p>
              <Button variant="outline" size="sm" className="mt-4 gap-1.5">
                <Plus className="h-3.5 w-3.5" />
                Add Event
              </Button>
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}

// --- CancelBookingDialog Component ---
interface CancelBookingDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onConfirm: () => void;
  isPending: boolean;
}

function CancelBookingDialog({ open, onOpenChange, onConfirm, isPending }: CancelBookingDialogProps) {
  return (
    <AlertDialog open={open} onOpenChange={onOpenChange}>
      <AlertDialogContent className="bg-white dark:bg-slate-800">
        <AlertDialogHeader>
          <AlertDialogTitle className="text-slate-800 dark:text-slate-100">Confirm Cancellation</AlertDialogTitle>
          <AlertDialogDescription className="text-slate-600 dark:text-slate-300">
            Are you sure you want to cancel this booking? This action cannot be undone.
            Consider notifying the client separately.
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel onClick={() => onOpenChange(false)} disabled={isPending}>
            Keep Booking
          </AlertDialogCancel>
          <AlertDialogAction
            onClick={onConfirm}
            className="bg-red-600 hover:bg-red-700"
            disabled={isPending}
          >
            {isPending ? 'Cancelling...' : 'Yes, Cancel Booking'}
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}

function CalendarPage() {
  const { profile } = useAuthWithProfile();
  const { useFetchBookings, updateBookingMutation } = useBookingQueries();
  const { useFetchUserProperties } = usePropertyQueries();

  const { data: userProperties = [] } = useFetchUserProperties(profile.user_id);
  const propertyIdsForBookings = userProperties.map(p => p.property_id);

  // Assuming useFetchBookings takes only propertyIds or handles enabled logic internally
  const { data: fetchedBookingsData = [] } = useFetchBookings(propertyIdsForBookings);

  const fetchedBookings: Booking2[] = (fetchedBookingsData || []).map((b: Booking) => ({
    ...b,
    start_datetime: new Date(b.start_datetime),
    end_datetime: new Date(b.end_datetime),
    created_at: new Date(b.created_at),
    updated_at: new Date(b.updated_at),
  }));

  const bookings: Booking2[] = fetchedBookings.length > 0 ? fetchedBookings : (import.meta.env.DEV ? mockBookings : []);

  const [currentWeekStart, setCurrentWeekStart] = useState(startOfWeek(new Date()));
  const [dayDetailOpen, setDayDetailOpen] = useState(false);
  const [selectedDayDate, setSelectedDayDate] = useState<Date | null>(null);
  const [cancelDialogOpen, setCancelDialogOpen] = useState(false);
  const [bookingToCancel, setBookingToCancel] = useState<string | null>(null);

  const firstWeekDays = Array.from({ length: 7 }, (_, i) => addDays(currentWeekStart, i));
  const secondWeekDays = Array.from({ length: 7 }, (_, i) => addDays(currentWeekStart, i + 7));

  const getBookingsForDate = (date: Date): Booking2[] => {
    return bookings.filter(booking => isSameDay(booking.start_datetime, date));
  };

  const navigateWeek = (direction: 'prev' | 'next' | 'today') => {
    setCurrentWeekStart(prev =>
      direction === 'prev' ? subDays(prev, 14) : direction === 'next' ? addDays(prev, 14) : startOfWeek(new Date())
    );
  };

  const handleDayClick = (date: Date) => {
    setSelectedDayDate(date);
    setDayDetailOpen(true);
  };

  const handleOpenCancelDialog = (bookingId: string) => {
    setBookingToCancel(bookingId);
    setCancelDialogOpen(true);
  };

  const confirmCancelBooking = () => {
    if (bookingToCancel) {
      updateBookingMutation.mutate({
        bookingId: bookingToCancel,
        data: { status: 'cancelled' }
      }, {
        onSuccess: () => {
          toast({ title: 'Booking Cancelled', description: 'The appointment has been marked as cancelled.' });
          setCancelDialogOpen(false);
          setBookingToCancel(null);
        },
        onError: (error) => {
          console.error('Error cancelling booking:', error);
          toast({
            title: 'Cancellation Failed',
            description: 'Could not cancel the booking. Please try again.',
            variant: 'destructive',
          });
        }
      });
    }
  };

  const WeekRow = ({ weekDays }: { weekDays: Date[] }) => (
    <div className="grid grid-cols-7 gap-3">
      {weekDays.map((date) => {
        const dayBookings = getBookingsForDate(date);
        const isCurrentDay = isToday(date);

        return (
          <div
            key={date.toISOString()}
            className={`min-h-[110px] p-2 pt-1.5 border rounded-lg transition-all hover:shadow-md cursor-pointer 
                        ${isCurrentDay ? 'bg-blue-50 border-blue-200 ring-2 ring-blue-100' : 'bg-white border-slate-200 hover:border-slate-300'}
                        dark:bg-slate-800 dark:border-slate-700 ${isCurrentDay ? 'dark:bg-blue-900/30 dark:border-blue-700' : 'dark:hover:border-slate-600'}`}
            onClick={() => handleDayClick(date)}
          >
            <div className={`text-sm font-semibold mb-2 ${isCurrentDay ? 'text-blue-700 dark:text-blue-300' : 'text-slate-700 dark:text-slate-300'}`}>
              {format(date, 'd')}
            </div>

            <div className="space-y-1">
              {dayBookings.slice(0, 2).map((booking) => (
                <div
                  key={booking.booking_id}
                  className={`text-xs px-2 py-1 rounded-md truncate ${booking.status === 'cancelled'
                    ? 'bg-red-100 text-red-600 line-through dark:bg-red-900/50 dark:text-red-400'
                    : 'bg-blue-100 text-blue-700 dark:bg-blue-900/50 dark:text-blue-300'}`}
                  title={`${format(booking.start_datetime, 'HH:mm')} - ${booking.visitor_name}`}
                >
                  {format(booking.start_datetime, 'HH:mm')} {booking.visitor_name.split(' ')[0]}
                </div>
              ))}

              {dayBookings.length > 2 && (
                <div className="text-xs text-slate-400 dark:text-slate-500">
                  +{dayBookings.length - 2} more
                </div>
              )}
            </div>

            {dayBookings.length === 0 && !isCurrentDay && (
              <div className="h-full flex items-center justify-center">
                <div className="w-2 h-2 bg-slate-200 dark:bg-slate-700 rounded-full"></div>
              </div>
            )}
          </div>
        );
      })}
    </div>
  );

  const selectedDayBookings = selectedDayDate ? getBookingsForDate(selectedDayDate) : [];

  return (
    <div className="grid grid-cols-2 gap-6">
      <div className="col-span-2 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 py-6">
        <div>
          <h1 className="text-3xl font-bold text-slate-800 dark:text-slate-100">Calendar</h1>
          <p className="text-base text-slate-500 dark:text-slate-400 mt-2">Manage your availability and view bookings.</p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <ShareCalendarModal>
            <Button variant="outline" size="default" className="gap-2 px-4 py-2">
              <Share2 className="h-4 w-4" />
              Share
            </Button>
          </ShareCalendarModal>
          <AvailabilityModal>
            <Button size="default" className="gap-2 bg-blue-600 hover:bg-blue-700 px-4 py-2">
              <Plus className="h-4 w-4" />
              Set Availability
            </Button>
          </AvailabilityModal>
        </div>
      </div>

      <Card className='col-span-2 shadow-sm'>
        <CardHeader className="p-8">
          <CardTitle className="flex items-center gap-2 text-xl">
            <CalendarIcon className="h-5 w-5 text-blue-600 dark:text-blue-400" />
            {format(currentWeekStart, 'MMMM d')} - {format(addDays(currentWeekStart, 13), 'MMMM d, yyyy')}
          </CardTitle>
          <CardAction className='flex items-center gap-3'>
            <Button variant="outline" onClick={() => navigateWeek('today')} size="default">Today</Button>
            <Button variant="outline" onClick={() => navigateWeek('prev')} aria-label="Previous two weeks" size="default"><ChevronLeft className="h-4 w-4" /></Button>
            <Button variant="outline" onClick={() => navigateWeek('next')} aria-label="Next two weeks" size="default"><ChevronRight className="h-4 w-4" /></Button>
          </CardAction>
        </CardHeader>
        <CardContent className="p-8 pt-0">
          <div className="grid grid-cols-7 gap-3 mb-4">
            {['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].map(day => (
              <div key={day} className="text-center text-sm font-semibold text-slate-600 dark:text-slate-400 pb-2">
                {day.toUpperCase()}
              </div>
            ))}
          </div>
          <WeekRow weekDays={firstWeekDays} />
          <div className="mb-4"></div>
          <WeekRow weekDays={secondWeekDays} />
        </CardContent>
      </Card>

      <AvailabilityModal>
        <Card className="p-0 border-slate-200 dark:border-slate-700 hover:border-blue-300 dark:hover:border-blue-600 transition-all duration-200 cursor-pointer hover:shadow-lg bg-white dark:bg-slate-800 rounded-xl">
          <CardContent className="p-8 flex flex-col items-center text-center gap-5">
            <div className="h-14 w-14 bg-blue-100 dark:bg-blue-900/50 rounded-xl flex items-center justify-center shrink-0">
              <Plus className="h-7 w-7 text-blue-600 dark:text-blue-400" />
            </div>
            <div>
              <h3 className="font-semibold text-xl text-slate-700 dark:text-slate-200 mb-3">Set Availability</h3>
              <p className="text-base text-slate-500 dark:text-slate-400 mb-6">Configure your weekly availability and time slots</p>
              <Button className="w-full py-3">Configure</Button>
            </div>
          </CardContent>
        </Card>
      </AvailabilityModal>

      <ShareCalendarModal>
        <Card className="p-0 border-slate-200 dark:border-slate-700 hover:border-green-300 dark:hover:border-green-600 transition-all duration-200 cursor-pointer hover:shadow-lg bg-white dark:bg-slate-800 rounded-xl">
          <CardContent className="p-8 flex flex-col items-center text-center gap-5">
            <div className="h-14 w-14 bg-green-100 dark:bg-green-900/50 rounded-xl flex items-center justify-center shrink-0">
              <Share2 className="h-7 w-7 text-green-600 dark:text-green-400" />
            </div>
            <div>
              <h3 className="font-semibold text-xl text-slate-700 dark:text-slate-200 mb-3">Share Calendar</h3>
              <p className="text-base text-slate-500 dark:text-slate-400 mb-6">Get your booking link to share with clients</p>
              <Button className="w-full py-3">Get Link</Button>
            </div>
          </CardContent>
        </Card>
      </ShareCalendarModal>

      <DayDetailDialog
        open={dayDetailOpen}
        onOpenChange={setDayDetailOpen}
        selectedDayDate={selectedDayDate}
        bookingsForDay={selectedDayBookings}
        onCancelBooking={handleOpenCancelDialog}
      />

      <CancelBookingDialog
        open={cancelDialogOpen}
        onOpenChange={setCancelDialogOpen}
        onConfirm={confirmCancelBooking}
        isPending={updateBookingMutation.isPending}
      />
    </div>
  );
}